// A share as a ring (UI-8): a tile's "how much of today's limit". The track
// is the neutral fill, the arc the state's colour, the figure in the middle.
// The number is written beside it as well — the ring only repeats it.

import { useId } from "react";
import { cn } from "@/lib/utils";
import type { Tone } from "@/components/emc/status";

const ARC: Record<Tone, string> = {
  running: "var(--chart-1)",
  scheduled: "var(--emc-indigo)",
  good: "var(--emc-success)",
  warn: "var(--emc-warning)",
  danger: "var(--destructive)",
  muted: "var(--emc-label-tertiary)",
};

interface Props {
  /** 0–1; more than 1 draws a full ring. */
  ratio: number | null;
  tone?: Tone;
  /** What the ring is a share of, for a screen reader. */
  label: string;
  className?: string;
}

export function Ring({ ratio, tone = "running", label, className }: Props) {
  const gradientId = useId();
  const size = 72;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const length = 2 * Math.PI * radius;
  const shown = ratio === null ? 0 : Math.min(Math.max(ratio, 0), 1);
  const percent = ratio === null ? "—" : `${Math.round(ratio * 100)}%`;
  const colour = ARC[tone];
  return (
    <span
      role="img"
      aria-label={`${label}: ${percent}`}
      className={cn("relative inline-flex size-18 items-center justify-center", className)}
    >
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 size-full -rotate-90" aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" style={{ stopColor: "var(--emc-info)" }} />
            <stop offset="1" style={{ stopColor: colour }} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} className="stroke-fill" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${shown * length} ${length}`}
          style={{ stroke: tone === "running" ? `url(#${gradientId})` : colour }}
          className="transition-[stroke-dasharray] duration-500"
        />
      </svg>
      <span className="relative text-detail font-semibold tabular-nums">{percent}</span>
    </span>
  );
}
